/**
 * StatusBadge Component
 * Module 07 - Dashboard UI
 * Section 05 - Models Management (Support Component)
 * 
 * Displays model status with appropriate colour styling
 */

import React from 'react';
import { cn } from '../../utils/cn';
import { ModelStatus } from '../../types';

interface StatusBadgeProps {
  status: ModelStatus;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Status badge component with colour-coded status indicators
 * 
 * @param status - Model processing status
 * @param size - Badge size (sm or md)
 * @param className - Additional CSS classes
 * 
 * @example
 * <StatusBadge status="ready" />
 * <StatusBadge status="processing" size="sm" />
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  status, 
  size = 'sm',
  className 
}) => {
  const baseStyles = 'inline-flex items-center font-semibold rounded-full';
  
  const sizes = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
  };

  const variants: Record<ModelStatus, string> = {
    uploaded: 'bg-blue-100 text-blue-800',
    processing: 'bg-yellow-100 text-yellow-800',
    ready: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };

  const labels: Record<ModelStatus, string> = {
    uploaded: 'Uploaded',
    processing: 'Processing',
    ready: 'Ready',
    failed: 'Failed',
  };

  return (
    <span 
      className={cn(
        baseStyles, 
        sizes[size], 
        variants[status],
        className
      )}
    >
      {labels[status]}
    </span>
  );
};

export default StatusBadge;
